import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';
import type { TaskColumnId } from '../../types';
import { BUILT_IN_COLUMN_MAP, getColumnWidthPx } from './columnRegistry';

interface InlineTaskRowProps {
  visibleColumns: TaskColumnId[];
  columnWidths: Record<string, number>;
  onConfirm: (title: string) => void;
  onCancel: () => void;
}

export function InlineTaskRow({
  visibleColumns,
  columnWidths,
  onConfirm,
  onCancel,
}: InlineTaskRowProps) {
  const { t } = useTranslation('tasks');
  const inputRef = useRef<HTMLInputElement>(null);
  const committedRef = useRef(false);

  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    const title = inputRef.current?.value.trim() ?? '';
    if (title) onConfirm(title);
    else onCancel();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      committedRef.current = true;
      onCancel();
    }
  }

  return (
    <div
      data-testid="tasks-inline-task-row"
      className="flex items-center gap-3 border-b border-border/50 bg-accent/20"
    >
      {/* Drag handle placeholder */}
      <div className="w-7 shrink-0" />

      {/* Checkbox placeholder */}
      <div className="w-5 shrink-0" />

      {/* Title cell */}
      <div className="flex w-56 min-w-0 shrink-0 items-center gap-2 p-3 md:w-80">
        <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" aria-hidden />
        <Input
          ref={inputRef}
          autoFocus
          data-testid="tasks-inline-task-row-input"
          placeholder={t('boardView.newTask.placeholder')}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          className="h-6 min-w-0 flex-1 border-border bg-background px-1 py-0 text-sm shadow-none"
        />
      </div>

      {/* Placeholder cells for the remaining visible columns — widths mirror ColumnHeader/TaskRow. */}
      {visibleColumns
        .filter((c) => c !== 'title')
        .map((col) => {
          const builtIn = BUILT_IN_COLUMN_MAP[col];
          if (!builtIn) return null;
          return (
            <div
              key={col}
              style={{ width: getColumnWidthPx(col, builtIn, columnWidths) }}
              className={cn('hidden shrink-0 p-3', builtIn.breakpoint)}
            />
          );
        })}

      {/* Overflow placeholder */}
      <div className="w-8 shrink-0" />
    </div>
  );
}
